import { observer } from "mobx-react-lite";
import { useEffect, useState } from "react";
import {
  Leaf,
  Send,
  CheckCircle2,
  Loader2,
  BarChart3,
  RefreshCw,
  Copy,
  Check,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { useGElementStore } from "../hooks";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";

const METRIC_CONFIG = {
  cumulative_kwh_v3: {
    category: "2_1",
    customerActivityDataId: "3bc5ef68-c048-48db-b583-b966ffab3176",
  },
  default: {
    category: "1_1",
    customerActivityDataId: "d52f3891-b6f8-475e-ae48-16e2dc7bb447",
  },
};

// ── CopyButton ─────────────────────────────────────────────────────────────────
const CopyButton = ({ text }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      const el = document.createElement("textarea");
      el.value = text;
      document.body.appendChild(el);
      el.select();
      document.execCommand("copy");
      document.body.removeChild(el);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <button
      onClick={handleCopy}
      title={copied ? "Copied!" : "Copy to clipboard"}
      className={`flex items-center gap-1 rounded-md border px-2 py-1
                        text-xs font-medium transition-all duration-200
                        ${
                          copied
                            ? // ✅ keep green — intentional feedback colour
                              "border-green-300 bg-green-50 text-green-600"
                            : // ✅ semantic tokens for idle state
                              "border-border bg-card text-muted-foreground hover:text-foreground"
                        }`}
    >
      {copied ? (
        <>
          <Check className="h-3 w-3" /> Copied!
        </>
      ) : (
        <>
          <Copy className="h-3 w-3" /> Copy
        </>
      )}
    </button>
  );
};

// ── Step Progress ──────────────────────────────────────────────────────────────
const StepProgress = ({ step1Done, step2Done }) => {
  const steps = [
    {
      n: 1,
      title: "Fetch Consumption",
      sub: "Select a time source to continue",
      done: step1Done,
      active: !step1Done,
    },
    {
      n: 2,
      title: "Calculate Emission",
      sub: "Click Get Emission to calculate",
      done: step2Done,
      active: step1Done && !step2Done,
    },
    {
      n: 3,
      title: "Publish to Senfi",
      sub: "Publish emission to platform",
      done: false,
      active: step2Done,
    },
  ];

  return (
    // ✅ bg-card + border-border
    <div className="flex items-center rounded-xl border border-border bg-card px-8 py-5 shadow-sm">
      {steps.map((s, i) => (
        <div key={s.n} className="flex flex-1 items-center">
          <div className="flex items-center gap-3">
            <div
              className={`flex h-10 w-10 shrink-0 items-center justify-center
                                         rounded-full text-sm font-bold transition-all
                                         ${
                                           s.done
                                             ? "bg-green-500 text-white"
                                             : s.active
                                               ? "bg-blue-600 text-white shadow-lg shadow-blue-200"
                                               : // ✅ bg-muted + text-muted-foreground
                                                 "bg-muted text-muted-foreground"
                                         }`}
            >
              {s.done ? <CheckCircle2 className="h-5 w-5" /> : s.n}
            </div>
            <div>
              <p
                className={`text-sm font-semibold
                                           ${
                                             s.active
                                               ? "text-blue-700 dark:text-blue-400"
                                               : s.done
                                                 ? "text-green-600 dark:text-green-400"
                                                 : // ✅ muted for inactive
                                                   "text-muted-foreground"
                                           }`}
              >
                {s.title}
              </p>
              {/* ✅ text-muted-foreground */}
              <p className="text-xs text-muted-foreground">{s.sub}</p>
            </div>
          </div>
          {i < 2 && (
            <div className="mx-4 flex-1">
              {/* ✅ bg-muted for track */}
              <div className="h-1 w-full rounded-full bg-muted">
                <div
                  className={`h-full rounded-full transition-all duration-700
                                                 ${s.done ? "w-full bg-green-400" : "w-0"}`}
                />
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

// ── Page ───────────────────────────────────────────────────────────────────────
const GElementPage = observer(() => {
  const [assetId, setAssetId] = useState("13358");
  const [measurementCode, setMeasurementCode] = useState(
    "advantech_energy_sensor_v3",
  );
  const [metricCode, setMetricCode] = useState("cumulative_kwh_v3");
  const [limit, setLimit] = useState("1");
  const [tmStart, setTmStart] = useState("");
  const [tmEnd, setTmEnd] = useState("");

  const {
    fetchConsumptionData,
    loadingConsumption,
    consumptionData,
    resetConsumption,
    fetchEmissionData,
    loadingEmission,
    emissionData,
    resetEmission,
    publishToSenfi,
    loadingPublish,
  } = useGElementStore();

  useEffect(() => {
    resetConsumption();
    return () => resetConsumption();
  }, []);

  const resetFlow = () => resetEmission();

  const handleFetchConsumption = () => {
    resetFlow();
    fetchConsumptionData({
      assetId,
      measurementCode,
      metricCode,
      limit,
      tmStart: tmStart ? new Date(tmStart).getTime() : "",
      tmEnd: tmEnd ? new Date(tmEnd).getTime() : "",
    });
  };

  const handleCalculateEmission = () => {
    if (!consumptionData?.length) return;
    const config = METRIC_CONFIG[metricCode] || METRIC_CONFIG.default;
    fetchEmissionData({
      companyId: "69bb4d189a03dd00017f84e3",
      category: config.category,
      customerActivityDataId: config.customerActivityDataId,
    });
  };

  const publishPayload = emissionData;

  const handlePublish = () => {
    if (!publishPayload) return;
    publishToSenfi();
  };

  const isFormReady = !!(
    assetId &&
    measurementCode &&
    metricCode &&
    tmStart &&
    tmEnd
  );
  const step1Done =
    Array.isArray(consumptionData) && consumptionData.length > 0;
  const step2Done = !!emissionData;

  const step2JSON = JSON.stringify(consumptionData ?? [], null, 2);
  const step3JSON = JSON.stringify(
    publishPayload ?? {
      measurement_code: "co2e",
      data: [
        { co2e: "pending…", sensor_id: "pending…", tm_source: "pending…" },
      ],
      type: "live",
    },
    null,
    2,
  );

  const btnBase = `flex w-full items-center justify-center gap-2 rounded-lg
                     py-2.5 text-sm font-semibold text-white transition
                     disabled:cursor-not-allowed disabled:opacity-50`;

  return (
    <div className="space-y-5 pb-12">
      {/* ── Header ─────────────────────────────────────────────────────── */}
      <div className="flex items-center gap-3">
        <div
          className="flex h-10 w-10 items-center justify-center
                                rounded-xl bg-green-600 text-white shadow"
        >
          <Leaf className="h-5 w-5" />
        </div>
        <div>
          {/* ✅ text-foreground */}
          <h1 className="text-xl font-bold text-foreground">G Element</h1>
          {/* ✅ text-muted-foreground */}
          <p className="text-sm text-muted-foreground">
            Fetch Electricity Consumption → Carbon Emission → Publish to Senfi
          </p>
        </div>
      </div>

      {/* ── Step Progress ───────────────────────────────────────────────── */}
      <StepProgress step1Done={step1Done} step2Done={step2Done} />

      {/* ── Three Columns ──────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
        {/* ══ STEP 1 ══ */}
        {/* ✅ bg-card + border-border */}
        <div className="flex flex-col rounded-xl border border-border bg-card shadow-sm">
          {/* ✅ border-border */}
          <div className="border-b border-border px-5 py-3">
            <p className="text-xs font-bold uppercase tracking-wider">
              {/* ✅ text-muted-foreground / text-foreground */}
              <span className="text-muted-foreground">Step 1 · </span>
              <span className="text-foreground">Fetch Consumption Data</span>
            </p>
          </div>

          <div className="flex flex-1 flex-col gap-3 p-5">
            <div>
              <Label className="mb-1">Asset ID</Label>
              <Input
                value={assetId}
                onChange={(e) => {
                  setAssetId(e.target.value);
                  resetFlow();
                }}
                placeholder="e.g. 13358"
              />
            </div>

            <div>
              <Label className="mb-1">Measurement Code</Label>
              <Input
                value={measurementCode}
                onChange={(e) => {
                  setMeasurementCode(e.target.value);
                  resetFlow();
                }}
                placeholder="e.g. advantech_energy_sensor_v3"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="mb-1">Metric</Label>
                <Input
                  value={metricCode}
                  onChange={(e) => {
                    setMetricCode(e.target.value);
                    resetFlow();
                  }}
                  placeholder="e.g. cumulative_kwh_v3"
                />
              </div>
              <div>
                <Label className="mb-1">Limit</Label>
                <Input
                  type="number"
                  value={limit}
                  onChange={(e) => setLimit(e.target.value)}
                  placeholder="1"
                  min="1"
                  max="10"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="mb-1">Time Start</Label>
                <Input
                  type="datetime-local"
                  value={tmStart}
                  onChange={(e) => setTmStart(e.target.value)}
                />
                {tmStart && (
                  // ✅ text-muted-foreground
                  <p className="mt-0.5 font-mono text-xs text-muted-foreground">
                    ms: {new Date(tmStart).getTime()}
                  </p>
                )}
              </div>
              <div>
                <Label className="mb-1">Time End</Label>
                <Input
                  type="datetime-local"
                  value={tmEnd}
                  onChange={(e) => setTmEnd(e.target.value)}
                />
                {tmEnd && (
                  <p className="mt-0.5 font-mono text-xs text-muted-foreground">
                    ms: {new Date(tmEnd).getTime()}
                  </p>
                )}
              </div>
            </div>
            <Button
              type="button"
              onClick={handleFetchConsumption}
              disabled={!isFormReady || loadingConsumption}
              className={`mt-auto ${btnBase} bg-green-600 hover:bg-green-700`}
            >
              {loadingConsumption ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" /> Fetching…
                </>
              ) : (
                <>
                  <RefreshCw className="h-4 w-4" /> Fetch Consumption Records
                </>
              )}
            </Button>
          </div>
        </div>

        {/* ══ STEP 2 ══ */}
        <div
          className={`flex flex-col rounded-xl border bg-card shadow-sm
                                 transition-all duration-300
                                 ${
                                   !step1Done
                                     ? "pointer-events-none opacity-50"
                                     : "border-border"
                                 }`}
        >
          <div className="border-b border-border px-5 py-3">
            <p className="text-xs font-bold uppercase tracking-wider">
              <span className="text-muted-foreground">Step 2 · </span>
              <span className="text-foreground">Calculate Emission</span>
            </p>
          </div>

          <div className="flex flex-1 flex-col gap-4 p-5">
            <div className="flex flex-col gap-1.5">
              <div className="flex items-center justify-between">
                {/* ✅ text-foreground */}
                <p className="text-xs font-semibold text-foreground">
                  Request Payload (Preview)
                </p>
                <CopyButton text={step2JSON} />
              </div>
              {/* ✅ intentionally keep dark bg-gray-900 — code block */}
              <pre
                className="h-72 overflow-auto rounded-lg bg-gray-900
                                             p-3 font-mono text-xs leading-relaxed
                                             text-emerald-300 transition-all duration-300"
              >
                {step2JSON}
              </pre>
            </div>
            <Button
              type="button"
              onClick={handleCalculateEmission}
              disabled={!step1Done || loadingEmission}
              className={`mt-auto ${btnBase} bg-green-600 hover:bg-green-700`}
            >
              {loadingEmission ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" /> Calculating…
                </>
              ) : (
                <>
                  <BarChart3 className="h-4 w-4" /> Get Emission
                </>
              )}
            </Button>
          </div>
        </div>

        {/* ══ STEP 3 ══ */}
        <div
          className={`flex flex-col rounded-xl border bg-card shadow-sm
                                 transition-all duration-300
                                 ${
                                   !step2Done
                                     ? "pointer-events-none opacity-50"
                                     : "border-border"
                                 }`}
        >
          <div className="border-b border-border px-5 py-3">
            <p className="text-xs font-bold uppercase tracking-wider">
              <span className="text-muted-foreground">Step 3 · </span>
              <span className="text-foreground">Publish to Senfi</span>
            </p>
          </div>

          <div className="flex flex-1 flex-col gap-4 p-5">
            <div className="flex flex-col gap-1.5">
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold text-foreground">
                  Request Payload (Preview)
                </p>
                <CopyButton text={step3JSON} />
              </div>
              {/* ✅ intentionally keep dark bg-gray-900 — code block */}
              <pre
                className="h-72 overflow-auto rounded-lg bg-gray-900
                                             p-3 font-mono text-xs leading-relaxed
                                             text-emerald-300 transition-all duration-300"
              >
                {step3JSON}
              </pre>
            </div>
            <Button
              type="button"
              onClick={handlePublish}
              disabled={!step2Done || loadingPublish}
              className={`mt-auto ${btnBase} bg-blue-600 hover:bg-blue-700`}
            >
              {loadingPublish ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" /> Publishing…
                </>
              ) : (
                <>
                  <Send className="h-4 w-4" /> Publish to Senfi
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
});

export default GElementPage;
